/**
 * Web Speech API (browser STT). Chrome/Edge/Safari (webkit) supported; Firefox limited.
 *
 * **Embeds (iframe / WebView):** The host page must allow microphone access, e.g.
 * `<iframe allow="microphone" src="…">`. The app requests `getUserMedia({ audio: true })`
 * before starting recognition so the permission prompt can appear in that context.
 */

/** Minimal surface used by PublicAgentChat (DOM lib may omit SpeechRecognition in some TS versions). */
export type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((ev: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
};

export type SpeechRecognitionResultEvent = Event & {
  /** Index of the first result that changed in this event. */
  resultIndex: number;
  results: SpeechRecognitionResultList;
};

export type SpeechRecognitionErrorEvent = Event & {
  /** e.g. `aborted`, `not-allowed`, `no-speech`, `network` */
  error: string;
};

export type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

export function getSpeechRecognitionConstructor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isWebSpeechRecognitionAvailable(): boolean {
  return getSpeechRecognitionConstructor() !== null;
}

/**
 * Use OpenAI Whisper (MediaRecorder → `agentTranscribe`) instead of browser Web Speech.
 *
 * **Why:** In Android WebView, many embedded WebViews, and some Flutter WebViews, `SpeechRecognition`
 * may exist and `getUserMedia` works (viz moves) but `onresult` never fires — no transcript.
 *
 * **Override:** `?voice_stt=webspeech` forces browser STT; `?voice_stt=whisper` forces Whisper.
 */
function isLikelyIOSOrIPadOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  /** iPadOS 13+ desktop UA with touch */
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Mobile browsers often expose SpeechRecognition but never populate results; use Whisper + MediaRecorder. */
function isLikelyMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (isLikelyIOSOrIPadOS()) return true;
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return true;
  /** Generic phone/tablet hints (KaiOS, some WebViews, etc.) */
  if (/Mobile|Mobi|IEMobile|BlackBerry|Opera Mini/i.test(ua)) return true;
  return false;
}

export function preferServerVoiceStt(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = new URLSearchParams(window.location.search).get('voice_stt');
    if (v === 'whisper' || v === 'server' || v === 'media') return true;
    if (v === 'webspeech' || v === 'browser') return false;
  } catch {
    /* */
  }
  if (isLikelyMobileBrowser()) return true;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/\bwv\b/i.test(ua) || /; wv\)/i.test(ua)) return true;
  if (/Flutter/i.test(ua)) return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  return false;
}

/** True when the UI should use Web Speech for live transcription (vs Whisper pipeline). */
export function shouldUseWebSpeechStt(): boolean {
  return isWebSpeechRecognitionAvailable() && !preferServerVoiceStt();
}

/** Request mic access (needed for iframe/WebView + primes permission before SpeechRecognition). */
export async function requestMicrophoneStream(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Microphone API unavailable');
  }
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

/**
 * Merge overlapping STT snapshots (refs + UI click snapshot). Prefer the longest string
 * when one fully contains another; otherwise join distinct parts.
 */
export function mergeVoiceTranscriptCandidates(parts: string[]): string {
  const cleaned = parts.map((p) => p.trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  if (cleaned.length === 1) return cleaned[0];
  const sorted = [...cleaned].sort((a, b) => b.length - a.length);
  const longest = sorted[0];
  if (sorted.every((s) => longest.includes(s))) return longest;
  return mergeSpeechFinalChunks(cleaned);
}

/**
 * Chrome often emits multiple `isFinal` segments where each extends or partially
 * repeats the same phrase. Joining them with spaces duplicates text. This
 * merges so we keep the longest cumulative segment when one is a prefix of the next.
 */
export function mergeSpeechFinalChunks(chunks: string[]): string {
  const parts = chunks.map((c) => c.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  let merged = parts[0];
  for (let i = 1; i < parts.length; i++) {
    const next = parts[i];
    if (next.startsWith(merged)) {
      merged = next;
      continue;
    }
    if (merged.startsWith(next)) {
      continue;
    }
    if (merged.endsWith(next) || next.endsWith(merged)) {
      merged = merged.length >= next.length ? merged : next;
      continue;
    }
    merged = `${merged} ${next}`;
  }
  return merged.replace(/\s+/g, ' ').trim();
}
